import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  FiActivity,
  FiCheckCircle,
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiXCircle,
} from "react-icons/fi";

import axios from "../../utills/api";

const EMPTY_FORM = {
  name: "",
  code: "",
  type: "Organic",
  description: "",
  isActive: true,
};

const SOURCE_TYPES = [
  "Organic",
  "Paid",
  "Social",
  "Referral",
  "Offline",
  "Website",
  "Other",
];

const LeadSources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);

  const getSourceArray = (response) => {
    const data = response?.data?.data;

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.sources)) {
      return data.sources;
    }

    if (Array.isArray(response?.data?.sources)) {
      return response.data.sources;
    }

    return [];
  };

  const fetchSources = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get("/lead-sources");

      setSources(getSourceArray(response));
    } catch (err) {
      console.error("Lead sources fetch error:", err);

      setError(
        err?.response?.data?.message ||
          "Unable to load lead sources."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const filteredSources = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return sources.filter((source) => {
      const name = String(source?.name || "").toLowerCase();
      const code = String(source?.code || "").toLowerCase();
      const description = String(
        source?.description || ""
      ).toLowerCase();

      const matchesSearch =
        !keyword ||
        name.includes(keyword) ||
        code.includes(keyword) ||
        description.includes(keyword);

      const active = source?.isActive !== false;

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && active) ||
        (statusFilter === "inactive" && !active);

      const matchesType =
        typeFilter === "all" ||
        String(source?.type || "") === typeFilter;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesType
      );
    });
  }, [sources, search, statusFilter, typeFilter]);

  const activeCount = sources.filter(
    (source) => source?.isActive !== false
  ).length;

  const inactiveCount = sources.filter(
    (source) => source?.isActive === false
  ).length;

  const totalLeads = sources.reduce(
    (sum, source) =>
      sum + Number(
        source?.leadCount ??
          source?.leadsCount ??
          source?.count ??
          0
      ),
    0
  );

  const openCreateModal = () => {
    setEditingSource(null);
    setForm(EMPTY_FORM);
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const openEditModal = (source) => {
    setEditingSource(source);

    setForm({
      name: source?.name || "",
      code: source?.code || "",
      type: source?.type || "Organic",
      description: source?.description || "",
      isActive: source?.isActive !== false,
    });

    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingSource(null);
    setForm(EMPTY_FORM);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } =
      event.target;

    setForm((previous) => ({
      ...previous,
      [name]:
        type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Lead source name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        type: form.type,
        description: form.description.trim(),
        isActive: form.isActive,
      };

      if (editingSource?._id || editingSource?.id) {
        const id =
          editingSource._id || editingSource.id;

        await axios.put(
          `/lead-sources/${id}`,
          payload
        );

        setSuccess(
          "Lead source updated successfully."
        );
      } else {
        await axios.post("/lead-sources", payload);

        setSuccess(
          "Lead source created successfully."
        );
      }

      setModalOpen(false);
      setEditingSource(null);
      setForm(EMPTY_FORM);

      await fetchSources();
    } catch (err) {
      console.error(
        "Lead source save error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Unable to save lead source."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (source) => {
    const id = source?._id || source?.id;

    if (!id) return;

    try {
      setError("");
      setSuccess("");

      const isActive = source?.isActive !== false;

      if (isActive) {
        await axios.put(
          `/lead-sources/${id}/deactivate`
        );

        setSuccess(
          "Lead source deactivated successfully."
        );
      } else {
        await axios.put(
          `/lead-sources/${id}/activate`
        );

        setSuccess(
          "Lead source activated successfully."
        );
      }

      await fetchSources();
    } catch (err) {
      console.error(
        "Lead source status error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Unable to update lead source status."
      );
    }
  };

  const confirmDelete = (source) => {
    setSourceToDelete(source);
    setDeleteModalOpen(true);
  };

  const deleteSource = async () => {
    if (!sourceToDelete) return;

    const id =
      sourceToDelete?._id ||
      sourceToDelete?.id;

    if (!id) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await axios.delete(`/lead-sources/${id}`);

      setDeleteModalOpen(false);
      setSourceToDelete(null);

      setSuccess(
        "Lead source deleted successfully."
      );

      await fetchSources();
    } catch (err) {
      console.error(
        "Lead source delete error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          "Unable to delete lead source."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col>
            <div>
              <h4 className="mb-1">
                Lead Sources
              </h4>

              <p className="text-muted mb-0">
                Manage the sources from which your
                education leads are generated.
              </p>
            </div>
          </Col>

          <Col
            xs="12"
            md="auto"
            className="mt-3 mt-md-0"
          >
            <div className="d-flex gap-2">
              <Button
                color="light"
                onClick={fetchSources}
                disabled={loading}
              >
                <FiRefreshCw
                  size={15}
                  className="me-1"
                />
                Refresh
              </Button>

              <Button
                color="primary"
                onClick={openCreateModal}
              >
                <FiPlus
                  size={15}
                  className="me-1"
                />
                Add Source
              </Button>
            </div>
          </Col>
        </Row>

        {/* Alerts */}
        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            color="success"
            toggle={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Summary */}
        <Row>
          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <FiActivity />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Total Sources
                    </p>
                    <h4 className="mb-0">
                      {sources.length}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <FiCheckCircle />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Active Sources
                    </p>
                    <h4 className="mb-0">
                      {activeCount}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <FiXCircle />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Inactive Sources
                    </p>
                    <h4 className="mb-0">
                      {inactiveCount}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="3" md="6">
            <Card>
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <div className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <FiActivity />
                    </div>
                  </div>

                  <div className="ms-3">
                    <p className="text-muted mb-1">
                      Leads Attributed
                    </p>
                    <h4 className="mb-0">
                      {totalLeads}
                    </h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card>
          <CardBody>
            <Row className="g-3">
              <Col lg="5">
                <Label className="form-label">
                  Search
                </Label>

                <div className="position-relative">
                  <FiSearch
                    size={16}
                    className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                  />

                  <Input
                    value={search}
                    onChange={(event) =>
                      setSearch(event.target.value)
                    }
                    placeholder="Search source name, code..."
                    className="ps-5"
                  />
                </div>
              </Col>

              <Col lg="3">
                <Label className="form-label">
                  Type
                </Label>

                <Input
                  type="select"
                  value={typeFilter}
                  onChange={(event) =>
                    setTypeFilter(event.target.value)
                  }
                >
                  <option value="all">
                    All Types
                  </option>

                  {SOURCE_TYPES.map((type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col lg="3">
                <Label className="form-label">
                  Status
                </Label>

                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    All Statuses
                  </option>
                  <option value="active">
                    Active
                  </option>
                  <option value="inactive">
                    Inactive
                  </option>
                </Input>
              </Col>

              <Col
                lg="1"
                className="d-flex align-items-end"
              >
                <Button
                  color="light"
                  className="w-100"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                    setTypeFilter("all");
                  }}
                  title="Clear filters"
                >
                  <FiFilter size={16} />
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Table */}
        <Card>
          <CardBody>
            <div className="table-responsive">
              <Table
                hover
                className="align-middle mb-0"
              >
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Lead Source</th>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Leads</th>
                    <th>Status</th>
                    <th className="text-end">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-5"
                      >
                        <Spinner size="sm" className="me-2" />
                        Loading lead sources...
                      </td>
                    </tr>
                  ) : filteredSources.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-5"
                      >
                        <div className="text-muted">
                          <FiActivity
                            size={30}
                            className="mb-2"
                          />

                          <div>
                            No lead sources found.
                          </div>

                          <small>
                            Try changing your filters
                            or create a new source.
                          </small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredSources.map(
                      (source, index) => {
                        const id =
                          source?._id ||
                          source?.id;

                        const active =
                          source?.isActive !== false;

                        const leads =
                          source?.leadCount ??
                          source?.leadsCount ??
                          source?.count ??
                          0;

                        return (
                          <tr key={id || index}>
                            <td>
                              {index + 1}
                            </td>

                            <td>
                              <div className="fw-medium">
                                {source?.name ||
                                  "Unnamed Source"}
                              </div>

                              {source?.description && (
                                <div className="text-muted small mt-1">
                                  {source.description}
                                </div>
                              )}
                            </td>

                            <td>
                              <span className="text-muted">
                                {source?.code || "—"}
                              </span>
                            </td>

                            <td>
                              <Badge
                                color="primary"
                                className="bg-primary-subtle text-primary"
                              >
                                {source?.type ||
                                  "Other"}
                              </Badge>
                            </td>

                            <td>
                              <span className="fw-medium">
                                {leads}
                              </span>
                            </td>

                            <td>
                              <Badge
                                color={
                                  active
                                    ? "success"
                                    : "secondary"
                                }
                                className={
                                  active
                                    ? "bg-success-subtle text-success"
                                    : "bg-secondary-subtle text-secondary"
                                }
                              >
                                {active
                                  ? "Active"
                                  : "Inactive"}
                              </Badge>
                            </td>

                            <td>
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() =>
                                    openEditModal(
                                      source
                                    )
                                  }
                                  title="Edit"
                                >
                                  <FiEdit2 size={14} />
                                </Button>

                                <Button
                                  color="light"
                                  size="sm"
                                  onClick={() =>
                                    toggleStatus(
                                      source
                                    )
                                  }
                                  title={
                                    active
                                      ? "Deactivate"
                                      : "Activate"
                                  }
                                >
                                  {active ? (
                                    <FiXCircle
                                      size={14}
                                    />
                                  ) : (
                                    <FiCheckCircle
                                      size={14}
                                    />
                                  )}
                                </Button>

                                <Button
                                  color="light"
                                  size="sm"
                                  className="text-danger"
                                  onClick={() =>
                                    confirmDelete(
                                      source
                                    )
                                  }
                                  title="Delete"
                                >
                                  <FiTrash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    )
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={closeModal}
        centered
      >
        <ModalHeader toggle={closeModal}>
          {editingSource
            ? "Edit Lead Source"
            : "Add Lead Source"}
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <Row className="g-3">
              <Col md="8">
                <Label className="form-label">
                  Source Name{" "}
                  <span className="text-danger">*</span>
                </Label>

                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Facebook Ads"
                  required
                />
              </Col>

              <Col md="4">
                <Label className="form-label">
                  Code
                </Label>

                <Input
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  placeholder="FACEBOOK"
                />
              </Col>

              <Col md="12">
                <Label className="form-label">
                  Source Type
                </Label>

                <Input
                  type="select"
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                >
                  {SOURCE_TYPES.map((type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md="12">
                <Label className="form-label">
                  Description
                </Label>

                <Input
                  type="textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe this lead source..."
                />
              </Col>

              <Col md="12">
                <div className="form-check form-switch">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    id="source-active"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleChange}
                  />

                  <Label
                    className="form-check-label"
                    htmlFor="source-active"
                  >
                    Active source
                  </Label>
                </div>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="light"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                <>
                  <FiCheckCircle
                    size={15}
                    className="me-1"
                  />
                  {editingSource
                    ? "Update Source"
                    : "Create Source"}
                </>
              )}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={() =>
          !saving && setDeleteModalOpen(false)
        }
        centered
      >
        <ModalHeader
          toggle={() =>
            !saving && setDeleteModalOpen(false)
          }
        >
          Delete Lead Source
        </ModalHeader>

        <ModalBody>
          <p className="mb-0">
            Are you sure you want to delete{" "}
            <strong>
              {sourceToDelete?.name ||
                "this lead source"}
            </strong>
            ?
          </p>

          <div className="alert alert-warning mt-3 mb-0">
            Existing leads associated with this
            source may prevent deletion depending
            on the backend rules.
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color="light"
            onClick={() =>
              setDeleteModalOpen(false)
            }
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            color="danger"
            onClick={deleteSource}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <FiTrash2
                  size={15}
                  className="me-1"
                />
                Delete
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default LeadSources;